import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteNav, SiteFooter } from "@/components/site/SiteChrome";
import { useLandingLang, grantsCta } from "@/components/FundingCaseStudy";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta, useJsonLd, faqLd } from "@/lib/seo";
import { COMPETITORS, CHECKED } from "@/lib/compare_text";

const NOT_LISTED = ["Not listed on plans", "No aparece en los planes"];

function Hero({ L, user }) {
  return (
    <section className="grid-bg border-b border-slate-200"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="eyebrow mb-4" data-testid="compare-eyebrow">{L.eyebrow}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-4xl" data-testid="compare-title">{L.title}</h1>
      <p className="mt-6 text-lg text-slate-600 max-w-2xl">{L.sub}</p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Link to={grantsCta(user)} data-testid="compare-hero-cta"><Button className="h-12 px-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-base">{L.cta}<ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        <Link to="/pricing" className="text-emerald-700 font-medium hover:underline" data-testid="compare-pricing-link">{L.pricing}</Link>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-4">{L.stats.map(([big, h, d], i) => (
        <div key={h} className="bg-white border border-slate-200 rounded-lg p-6 card-lift" style={{ animation: `fadeUp 500ms ease ${i * 90}ms both` }} data-testid={`compare-stat-${i}`}>
          <div className="font-display text-4xl font-extrabold text-emerald-600">{big}</div>
          <div className="mt-2 font-semibold text-slate-900">{h}</div><p className="text-sm text-slate-500 mt-1">{d}</p>
        </div>))}</div>
    </div></section>
  );
}

function Table({ L, name }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{L.tableTitle}</h2>
      <p className="sm:hidden mt-2 text-xs text-slate-500" data-testid="compare-scroll-hint">{L.swipe} {name} →</p>
      <div className="mt-8 overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="compare-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="bg-slate-900 text-white text-left"><th className="p-4 font-medium w-1/3">{L.feature}</th><th className="p-4 font-semibold text-emerald-400">GrantVerify</th><th className="p-4 font-medium text-slate-300">{name}</th></tr></thead>
          <tbody>{L.rows.map(([f, us, them], i) => (
            <tr key={f} className="border-t border-slate-200 align-top" data-testid={`compare-row-${i}`}>
              <td className="p-4 font-medium text-slate-900">{f}</td>
              <td className="p-4 bg-emerald-50/60 text-slate-900"><span className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />{us}</span></td>
              <td className="p-4 text-slate-500"><span className="flex gap-2">{NOT_LISTED.includes(them) && <Minus className="h-4 w-4 mt-0.5 text-slate-300 shrink-0" />}{them}</span></td>
            </tr>))}</tbody>
        </table>
      </div>
    </section>
  );
}

function Reasons({ L }) {
  return (
    <section className="bg-slate-900 text-white"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <h2 className="font-display text-3xl font-extrabold tracking-tight">{L.reasonsTitle}</h2>
      <div className="mt-10 grid md:grid-cols-2 gap-6">{L.reasons.map(([h, d], i) => (
        <div key={h} className="border border-slate-700 rounded-lg p-8" data-testid={`compare-reason-${i}`}><div className="font-mono text-xs text-emerald-400">{String(i + 1).padStart(2, "0")}</div><h3 className="font-display text-xl font-bold mt-2">{h}</h3><p className="text-slate-300 mt-3 text-sm leading-relaxed">{d}</p></div>))}</div>
      <div className="mt-12 border-t border-slate-700 pt-10" data-testid="compare-fit"><h3 className="font-display text-xl font-bold text-emerald-400">{L.fit[0]}</h3>
        <ul className="mt-4 grid sm:grid-cols-2 gap-3">{L.fit[1].map((x) => <li key={x} className="flex gap-2 text-slate-200"><Check className="h-5 w-5 text-emerald-400 shrink-0" />{x}</li>)}</ul></div>
    </div></section>
  );
}

function Faq({ L }) {
  useJsonLd("compare-faq", faqLd(L.faq));
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="compare-faq">
      <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{L.faqTitle}</h2>
      <div className="mt-8 divide-y divide-slate-200">{L.faq.map(([q, a]) => <div key={q} className="py-5"><h3 className="font-semibold text-slate-900">{q}</h3><p className="text-slate-600 mt-2">{a}</p></div>)}</div>
    </section>
  );
}

export default function Compare() {
  const { slug } = useParams();
  const { user } = useAuth();
  const lang = useLandingLang();
  const c = COMPETITORS[slug];
  const L = c?.[lang];
  usePageMeta(L?.meta || COMPETITORS.instrumentl.en.meta);
  if (!c) return <Navigate to="/pricing" replace />;
  const other = slug === "instrumentl" ? "quillify" : "instrumentl";
  return (
    <div className="bg-slate-50 min-h-screen" data-testid={`compare-page-${slug}`}>
      <SiteNav />
      <main data-no-translate lang={lang}>
        <Hero L={L} user={user} />
        <Table L={L} name={c.name} />
        <Reasons L={L} />
        <Faq L={L} />
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="bg-emerald-600 text-white rounded-lg p-8 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <Link to={`/compare/${other}`} className="font-display text-xl font-bold hover:underline underline-offset-4" data-testid="compare-other-link">{L.other}</Link>
            <Link to={grantsCta(user)} data-testid="compare-bottom-cta"><Button className="h-12 px-7 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 text-base">{L.cta}<ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
          <p className="mt-6 text-xs text-slate-400" data-testid="compare-note">{L.note} {CHECKED[lang]}. {L.tm}</p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
